<template>
  <div class="password-generator">
    <h1>密码生成器</h1>
    
    <div class="generator-container">
      <!-- 生成结果 -->
      <div class="result-section">
        <div class="password-display">
          <el-input
            v-model="generatedPassword"
            readonly
            :type="showPassword ? 'text' : 'password'"
          >
            <template #append>
              <el-button @click="togglePasswordVisibility">
                <el-icon>
                  <component :is="showPassword ? 'View' : 'Hide'" />
                </el-icon>
              </el-button>
              <el-button @click="copyPassword">
                <el-icon><DocumentCopy /></el-icon>
              </el-button>
            </template>
          </el-input>
        </div>
        
        <div class="strength-meter">
          <div class="strength-label">密码强度：</div>
          <div class="strength-bars">
            <div
              v-for="n in 4"
              :key="n"
              class="strength-bar"
              :class="{ active: passwordStrength >= n }"
            ></div>
          </div>
          <div class="strength-text">{{ strengthText }}</div>
        </div>
      </div>

      <!-- 生成选项 -->
      <div class="options-section">
        <div class="length-option">
          <span>密码长度：{{ length }} 位</span>
          <el-slider
            v-model="length"
            :min="4"
            :max="64"
            @change="generatePassword"
          />
        </div>

        <div class="character-options">
          <el-checkbox v-model="options.uppercase" @change="generatePassword">
            大写字母 (A-Z)
          </el-checkbox>
          <el-checkbox v-model="options.lowercase" @change="generatePassword">
            小写字母 (a-z)
          </el-checkbox>
          <el-checkbox v-model="options.numbers" @change="generatePassword">
            数字 (0-9)
          </el-checkbox>
          <el-checkbox v-model="options.symbols" @change="generatePassword">
            特殊字符 (!@#$%^&*)
          </el-checkbox>
          <el-checkbox v-model="options.excludeSimilar" @change="generatePassword">
            排除相似字符 (l, 1, I, O, 0)
          </el-checkbox>
          <el-checkbox v-model="options.excludeAmbiguous" @change="generatePassword">
            排除易混淆字符 ({}, [], (), /)
          </el-checkbox>
        </div>

        <div class="action-buttons">
          <el-button type="primary" @click="generatePassword">
            生成新密码
          </el-button>
          <el-button @click="savePassword" :disabled="!generatedPassword">
            保存密码
          </el-button>
        </div>
      </div>

      <!-- 历史记录 -->
      <div class="history-section" v-if="passwordHistory.length > 0">
        <h3>历史记录</h3>
        <el-table :data="passwordHistory" style="width: 100%">
          <el-table-column prop="password" label="密码" width="300">
            <template #default="scope">
              <span class="password-cell">
                {{ scope.row.visible ? scope.row.password : '••••••••' }}
                <el-button
                  link
                  type="primary"
                  @click="toggleHistoryPasswordVisibility(scope.$index)"
                >
                  <el-icon>
                    <component :is="scope.row.visible ? 'View' : 'Hide'" />
                  </el-icon>
                </el-button>
              </span>
            </template>
          </el-table-column>
          <el-table-column prop="length" label="长度" width="100" />
          <el-table-column prop="strength" label="强度" width="100">
            <template #default="scope">
              <el-tag :type="getStrengthType(scope.row.strength)">
                {{ getStrengthText(scope.row.strength) }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column prop="time" label="生成时间" />
          <el-table-column label="操作" width="120">
            <template #default="scope">
              <el-button
                link
                type="primary"
                @click="copyHistoryPassword(scope.row.password)"
              >
                复制
              </el-button>
              <el-button
                link
                type="danger"
                @click="removeFromHistory(scope.$index)"
              >
                删除
              </el-button>
            </template>
          </el-table-column>
        </el-table>
      </div>

      <!-- 说明区域 -->
      <div class="info-section">
        <h3>安全提示</h3>
        <ul>
          <li>建议使用12位以上的密码长度</li>
          <li>混合使用大小写字母、数字和特殊字符</li>
          <li>避免使用个人信息作为密码</li>
          <li>不同账号使用不同的密码</li>
          <li>定期更换密码</li>
        </ul>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, computed } from 'vue'
import { ElMessage } from 'element-plus'
import { View, Hide, DocumentCopy } from '@element-plus/icons-vue'

const generatedPassword = ref('')
const showPassword = ref(false)
const length = ref(16)
const passwordHistory = ref([])

const options = reactive({
  uppercase: true,
  lowercase: true,
  numbers: true,
  symbols: true,
  excludeSimilar: false,
  excludeAmbiguous: false
})

// 字符集
const charSets = {
  uppercase: 'ABCDEFGHIJKLMNOPQRSTUVWXYZ',
  lowercase: 'abcdefghijklmnopqrstuvwxyz',
  numbers: '0123456789',
  symbols: '!@#$%^&*',
  similar: 'Il1O0',
  ambiguous: '{}[]()/\\\'"`~,;:.<>'
}

// 生成密码
const generatePassword = () => {
  let chars = ''
  if (!options.uppercase && !options.lowercase && !options.numbers && !options.symbols) {
    ElMessage.warning('请至少选择一种字符类型')
    return
  }

  if (options.uppercase) chars += charSets.uppercase
  if (options.lowercase) chars += charSets.lowercase
  if (options.numbers) chars += charSets.numbers
  if (options.symbols) chars += charSets.symbols

  if (options.excludeSimilar) {
    chars = chars.split('').filter(char => !charSets.similar.includes(char)).join('')
  }
  if (options.excludeAmbiguous) {
    chars = chars.split('').filter(char => !charSets.ambiguous.includes(char)).join('')
  }

  let password = ''
  const array = new Uint32Array(length.value)
  crypto.getRandomValues(array)
  
  for (let i = 0; i < length.value; i++) {
    password += chars.charAt(array[i] % chars.length)
  }

  generatedPassword.value = password
}

// 复制密码
const copyPassword = async () => {
  if (!generatedPassword.value) {
    ElMessage.warning('请先生成密码')
    return
  }

  try {
    await navigator.clipboard.writeText(generatedPassword.value)
    ElMessage.success('密码已复制到剪贴板')
  } catch (error) {
    ElMessage.error('复制失败')
  }
}

// 切换密码可见性
const togglePasswordVisibility = () => {
  showPassword.value = !showPassword.value
}

// 保存密码到历史记录
const savePassword = () => {
  if (!generatedPassword.value) return
  
  passwordHistory.value.unshift({
    password: generatedPassword.value,
    length: generatedPassword.value.length,
    strength: passwordStrength.value,
    time: new Date().toLocaleString(),
    visible: false
  })
}

// 从历史记录中删除
const removeFromHistory = (index) => {
  passwordHistory.value.splice(index, 1)
}

// 复制历史密码
const copyHistoryPassword = async (password) => {
  try {
    await navigator.clipboard.writeText(password)
    ElMessage.success('密码已复制到剪贴板')
  } catch (error) {
    ElMessage.error('复制失败')
  }
}

// 切换历史密码可见性
const toggleHistoryPasswordVisibility = (index) => {
  passwordHistory.value[index].visible = !passwordHistory.value[index].visible
}

// 计算密码强度
const passwordStrength = computed(() => {
  const password = generatedPassword.value
  if (!password) return 0

  let strength = 0
  if (password.length >= 12) strength++
  if (/[A-Z]/.test(password)) strength++
  if (/[a-z]/.test(password)) strength++
  if (/[0-9]/.test(password)) strength++
  if (/[^A-Za-z0-9]/.test(password)) strength++
  
  return Math.min(strength, 4)
})

// 密码强度文本
const strengthText = computed(() => {
  switch (passwordStrength.value) {
    case 0: return '非常弱'
    case 1: return '弱'
    case 2: return '中等'
    case 3: return '强'
    case 4: return '非常强'
    default: return ''
  }
})

// 获取强度标签类型
const getStrengthType = (strength) => {
  switch (strength) {
    case 1: return 'danger'
    case 2: return 'warning'
    case 3: return 'success'
    case 4: return 'success'
    default: return 'info'
  }
}

// 获取强度文本
const getStrengthText = (strength) => {
  switch (strength) {
    case 1: return '弱'
    case 2: return '中等'
    case 3: return '强'
    case 4: return '非常强'
    default: return '未知'
  }
}

// 初始生成密码
generatePassword()
</script>

<style scoped>
.password-generator {
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px;
  margin-top: 80px;
}

.generator-container {
  margin-top: 20px;
}

.result-section {
  margin: 20px 0;
}

.password-display {
  margin-bottom: 20px;
}

.strength-meter {
  display: flex;
  align-items: center;
  gap: 10px;
}

.strength-bars {
  display: flex;
  gap: 5px;
}

.strength-bar {
  width: 40px;
  height: 8px;
  background-color: #DCDFE6;
  border-radius: 4px;
  transition: background-color 0.3s;
}

.strength-bar.active:nth-child(1) { background-color: #F56C6C; }
.strength-bar.active:nth-child(2) { background-color: #E6A23C; }
.strength-bar.active:nth-child(3) { background-color: #67C23A; }
.strength-bar.active:nth-child(4) { background-color: #409EFF; }

.options-section {
  margin: 20px 0;
  padding: 20px;
  background-color: #f5f7fa;
  border-radius: 8px;
}

.length-option {
  margin-bottom: 20px;
}

.character-options {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 15px;
  margin-bottom: 20px;
}

.action-buttons {
  display: flex;
  gap: 15px;
  justify-content: center;
}

.history-section {
  margin: 20px 0;
}

.password-cell {
  display: flex;
  align-items: center;
  gap: 10px;
  font-family: monospace;
}

.info-section {
  margin-top: 30px;
  padding: 20px;
  background-color: #f5f7fa;
  border-radius: 8px;
}

.info-section ul {
  padding-left: 20px;
  margin: 10px 0;
}

.info-section li {
  margin: 8px 0;
  line-height: 1.5;
}

:deep(.el-input-group__append) {
  padding: 0;
}

:deep(.el-input-group__append button) {
  border: none;
  margin: 0;
  height: 100%;
}
</style> 